/**
 * WRMS Leave Balance Service — Digi Sahyadri
 * Manages leave balances per employee per year in Firestore.
 */

import { db } from '../../config/firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  orderBy,
} from 'firebase/firestore';

const COLLECTION = 'wrms_leave_balance';

const DEFAULT_BALANCE = {
  casual_leave: 12,
  sick_leave: 12,
  emergency_leave: 3,
  paid_leave: 15,
};

// Normalize leave type names
const normalizeLeaveType = (leaveType) => {
  const map = {
    'casual': 'casual_leave',
    'casual leave': 'casual_leave',
    'sick': 'sick_leave',
    'sick leave': 'sick_leave',
    'emergency': 'emergency_leave',
    'emergency leave': 'emergency_leave',
    'paid': 'paid_leave',
    'paid leave': 'paid_leave',
  };
  return map[(leaveType || '').toLowerCase()] || leaveType;
};

const getDocId = (employeeId, year) => `${employeeId}_${year}`;

/**
 * Get leave balance for an employee for the given year.
 * If no record exists, returns defaults.
 */
export const getBalance = async (employeeId, year = null) => {
  const yr = year || new Date().getFullYear();
  const docRef = doc(db, COLLECTION, getDocId(employeeId, yr));
  const snap = await getDoc(docRef);

  if (snap.exists()) {
    return { id: snap.id, ...snap.data() };
  }

  // Return defaults
  return {
    employeeId,
    year: yr,
    ...DEFAULT_BALANCE,
    updatedAt: null,
    isDefault: true,
  };
};

/**
 * Set/update leave balance for an employee (HR/Admin)
 */
export const setBalance = async (employeeId, year, balances, updatedBy = null) => {
  const yr = year || new Date().getFullYear();
  const docRef = doc(db, COLLECTION, getDocId(employeeId, yr));

  await setDoc(docRef, {
    employeeId,
    year: yr,
    ...balances,
    updatedAt: serverTimestamp(),
    updatedBy: updatedBy || null,
  }, { merge: true });

  return { success: true };
};

/**
 * Deduct days from an employee's leave balance.
 * Called on final HR approval.
 */
export const deductLeave = async (employeeId, leaveType, days) => {
  const yr = new Date().getFullYear();
  const docRef = doc(db, COLLECTION, getDocId(employeeId, yr));
  const snap = await getDoc(docRef);

  const field = normalizeLeaveType(leaveType);
  const currentBalance = snap.exists() ? (snap.data()[field] ?? DEFAULT_BALANCE[field] ?? 0) : (DEFAULT_BALANCE[field] ?? 0);
  const newBalance = Math.max(0, currentBalance - days);

  if (snap.exists()) {
    await updateDoc(docRef, {
      [field]: newBalance,
      updatedAt: serverTimestamp(),
    });
  } else {
    const newData = { employeeId, year: yr, ...DEFAULT_BALANCE, [field]: newBalance, updatedAt: serverTimestamp() };
    await setDoc(docRef, newData);
  }

  return { success: true, field, previousBalance: currentBalance, newBalance };
};

/**
 * Restore/Refund days to an employee's leave balance when an approved leave is cancelled.
 */
export const restoreLeave = async (employeeId, leaveType, days) => {
  const yr = new Date().getFullYear();
  const docRef = doc(db, COLLECTION, getDocId(employeeId, yr));
  const snap = await getDoc(docRef);

  const field = normalizeLeaveType(leaveType);
  const currentBalance = snap.exists() ? (snap.data()[field] ?? DEFAULT_BALANCE[field] ?? 0) : (DEFAULT_BALANCE[field] ?? 0);
  const newBalance = currentBalance + Number(days || 0);

  if (snap.exists()) {
    await updateDoc(docRef, {
      [field]: newBalance,
      updatedAt: serverTimestamp(),
    });
  } else {
    const newData = { employeeId, year: yr, ...DEFAULT_BALANCE, [field]: newBalance, updatedAt: serverTimestamp() };
    await setDoc(docRef, newData);
  }

  return { success: true, field, previousBalance: currentBalance, newBalance };
};

/**
 * Credit Comp Off days (on Extra Work approval)
 */
export const creditCompOff = async (employeeId, days) => {
  const yr = new Date().getFullYear();
  const docRef = doc(db, COLLECTION, getDocId(employeeId, yr));
  const snap = await getDoc(docRef);

  const current = snap.exists() ? (snap.data().comp_off ?? 0) : 0;
  const newBalance = current + days;

  if (snap.exists()) {
    await updateDoc(docRef, { comp_off: newBalance, updatedAt: serverTimestamp() });
  } else {
    await setDoc(docRef, { employeeId, year: yr, ...DEFAULT_BALANCE, comp_off: newBalance, updatedAt: serverTimestamp() });
  }

  return { success: true, newBalance };
};

/**
 * Check if employee has sufficient balance.
 * Returns { sufficient: bool, available: number }
 */
export const checkBalance = async (employeeId, leaveType, daysRequested) => {
  const balance = await getBalance(employeeId);
  const field = normalizeLeaveType(leaveType);
  const available = balance[field] ?? 0;
  return {
    sufficient: available >= daysRequested,
    available,
    field,
  };
};

/**
 * Get all employees' leave balances for a given year (HR/Admin view)
 */
export const getAllBalances = async (year = null) => {
  const yr = year || new Date().getFullYear();
  const snap = await getDocs(collection(db, COLLECTION));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(b => b.year === yr);
};

export { DEFAULT_BALANCE, normalizeLeaveType };
